import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Select, MenuItem, FormControl,
  IconButton, Tooltip, CircularProgress, Chip, Stack, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, alpha, useTheme, Container, Collapse, TextField, InputAdornment,
  Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress
} from '@mui/material';
import axios from '../utils/axios';

const PRIORITY_COLORS = { HIGH: '#EF4444', MEDIUM: '#F59E0B', LOW: '#6B7280' };
const FINDING_COLORS = {
  UNDERUTILIZED: '#EF4444',
  OVERPROVISIONED: '#F59E0B',
  RIGHT_SIZED: '#10B981',
  UNDERSIZED: '#8B5CF6',
  INSUFFICIENT_DATA: '#6B7280',
  ERROR: '#6B7280'
};
const FINDING_LABELS = {
  UNDERUTILIZED: 'Underutilized',
  OVERPROVISIONED: 'Overprovisioned',
  RIGHT_SIZED: 'Right-sized',
  UNDERSIZED: 'Undersized',
  INSUFFICIENT_DATA: 'No Data',
  ERROR: 'Error'
};
const CATEGORY_LABELS = {
  SAVINGS_PLANS: 'Savings Plans',
  RESERVED_INSTANCES: 'Reserved Instances',
  RIGHTSIZING: 'Right-sizing',
  SPOT_INSTANCES: 'Spot Instances',
  STORAGE_OPTIMIZATION: 'Storage',
  DATA_TRANSFER: 'Data Transfer',
  LOG_OPTIMIZATION: 'Log Optimization',
  IDLE_RESOURCES: 'Idle Resources',
  CACHING: 'Caching',
  VERSION_UPGRADE: 'Version Upgrade',
  ANOMALY: 'Anomaly',
  MODERNIZATION: 'Modernization',
  ARCHITECTURE: 'Architecture',
  COMMITMENT_DISCOUNT: 'Commitment Discount',
};

/* Outline SVG icons */
const SavingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2v16" strokeLinecap="round" />
    <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const OpportunityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2l2 4h4l-3 3 1 5-4-2-4 2 1-5-3-3h4l2-4z" strokeLinejoin="round" />
  </svg>
);
const HighPriorityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 3l7 12H3l7-12z" strokeLinejoin="round" />
    <path d="M10 8v3M10 13.5v.01" strokeLinecap="round" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 10a7 7 0 0112.9-3.7M17 10a7 7 0 01-12.9 3.7" strokeLinecap="round" />
    <path d="M16 3v4h-4M4 17v-4h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="9" cy="9" r="6" />
    <path d="M14 14l4 4" strokeLinecap="round" />
  </svg>
);
const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="5" y="5" width="9" height="9" rx="1" />
    <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
  </svg>
);
const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronUp = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 10l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function CostOpportunities() {
  const theme = useTheme();
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [timeRange, setTimeRange] = useState('30');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [expandedRows, setExpandedRows] = useState({});

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Rightsizing state
  const [rightsizingData, setRightsizingData] = useState(null);
  const [isLoadingRightsizing, setIsLoadingRightsizing] = useState(false);
  const [rightsizingError, setRightsizingError] = useState(null);

  // Metrics config dialog
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [metricsConfig, setMetricsConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ provider: 'prometheus', endpoint: '', authType: 'none', authToken: '' });
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { if (selectedAccount) loadOpportunities(); }, [selectedAccount, timeRange]);

  const loadAccounts = async () => {
    try {
      const res = await axios.get('/api/aws/accounts');
      const accounts = res.data.accounts || [];
      setAwsAccounts(accounts);
      if (accounts.length > 0) setSelectedAccount(accounts[0].id);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const loadOpportunities = async (forceRefresh = false) => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ timeRange });
      if (forceRefresh) params.append('forceRefresh', 'true');
      const res = await axios.get(`/api/cost/opportunities/${selectedAccount}?${params}`);
      setData(res.data);
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.message || err.message || 'Failed to load opportunities');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRightsizing = async (forceRefresh = false) => {
    if (!selectedAccount) return;
    setIsLoadingRightsizing(true);
    setRightsizingError(null);
    try {
      const params = forceRefresh ? '?forceRefresh=true' : '';
      const res = await axios.get(`/api/cost/rightsizing/${selectedAccount}${params}`);
      setRightsizingData(res.data);
    } catch (err) {
      const errData = err.response?.data;
      setRightsizingError(errData?.message || err.message || 'Failed to load rightsizing data');
      setRightsizingData(null);
    } finally {
      setIsLoadingRightsizing(false);
    }
  };

  const loadMetricsConfig = async () => {
    if (!selectedAccount) return;
    try {
      const res = await axios.get(`/api/cost/metrics-config/${selectedAccount}`);
      setMetricsConfig(res.data.configs || []);
    } catch (err) {
      setMetricsConfig([]);
    }
  };

  const saveMetricsConfig = async () => {
    if (!selectedAccount) return;
    setIsSavingConfig(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/api/cost/metrics-config/${selectedAccount}`, configForm);
      const { connectionStatus } = res.data;
      setTestResult({
        status: connectionStatus === 'connected' ? 'success' : 'error',
        message: connectionStatus === 'connected'
          ? 'Configuration saved and connection verified successfully.'
          : 'Configuration saved but connection test failed. Please verify the endpoint and credentials.'
      });
      await loadMetricsConfig();
      if (connectionStatus === 'connected') {
        // Auto-close after short delay on success
        setTimeout(() => { setConfigDialogOpen(false); loadRightsizing(true); }, 2000);
      }
    } catch (err) {
      setTestResult({ status: 'error', message: err.response?.data?.error || err.message || 'Failed to save configuration' });
    } finally {
      setIsSavingConfig(false);
    }
  };

  const testConnection = async () => {
    if (!selectedAccount || !configForm.endpoint) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/api/cost/metrics-config/${selectedAccount}/test`, configForm);
      const { status, responseTimeMs, errorMessage, buildInfo, queryTest } = res.data;
      if (status === 'connected') {
        let msg = `Connection successful (${responseTimeMs}ms)`;
        if (buildInfo?.version) msg += ` — Prometheus v${buildInfo.version}`;
        if (queryTest?.targetsFound !== undefined) msg += ` — ${queryTest.targetsFound} target(s) reporting`;
        setTestResult({ status: 'success', message: msg });
      } else {
        setTestResult({ status: 'error', message: errorMessage || 'Connection failed' });
      }
    } catch (err) {
      setTestResult({ status: 'error', message: err.response?.data?.error || err.message || 'Test request failed' });
    } finally {
      setIsTesting(false);
    }
  };

  // Load rightsizing when tab switches to it
  useEffect(() => {
    if (activeTab === 1 && selectedAccount && !rightsizingData && !isLoadingRightsizing) {
      loadRightsizing();
      loadMetricsConfig();
    }
  }, [activeTab, selectedAccount]);

  const fmt = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  };

  const filteredOpportunities = useMemo(() => {
    if (!data?.opportunities) return [];
    return data.opportunities.filter(opp => {
      if (filterPriority !== 'ALL' && opp.priority !== filterPriority) return false;
      if (filterCategory !== 'ALL' && opp.category !== filterCategory) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return opp.title.toLowerCase().includes(q) ||
          opp.service.toLowerCase().includes(q) ||
          opp.description.toLowerCase().includes(q);
      }
      return true;
    });
  }, [data, filterPriority, filterCategory, searchQuery]);

  const categories = useMemo(() => {
    if (!data?.opportunities) return [];
    const cats = [...new Set(data.opportunities.map(o => o.category))];
    return cats.sort();
  }, [data]);

  const toggleRow = (idx) => {
    setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const noAccounts = awsAccounts.length === 0 && !isLoading;

  const StatCard = ({ icon, iconColor, iconBg, title, value, subtitle }) => (
    <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px',
            bgcolor: theme.palette.mode === 'light' ? iconBg : alpha(iconColor, 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor
          }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>{title}</Typography>
        </Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 0.25 }}>{value}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{subtitle}</Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 0.25 }}>Opportunities</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Cost optimization suggestions based on your usage patterns</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {awsAccounts.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}
                sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                {awsAccounts.map(a => (
                  <MenuItem key={a.id} value={a.id} sx={{ fontSize: '0.8125rem' }}>
                    {a.account_name || a.account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}
              sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="14">Last 14 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="60">Last 60 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => loadOpportunities(true)} disabled={isLoading}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 } }}>
        <Tab label="Cost Opportunities" />
        <Tab label="Rightsizing" />
      </Tabs>

      {/* ═══ TAB 0: Cost Opportunities ═══ */}
      {activeTab === 0 && (
      <>
      {/* Loading */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={36} sx={{ mb: 2 }} />
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Analyzing your AWS costs for optimization opportunities...</Typography>
        </Box>
      )}

      {/* No accounts */}
      {noAccounts && (
        <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>No AWS Accounts Connected</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Connect an AWS account to discover cost optimization opportunities.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && !isLoading && (
        <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>{error}</Alert>
      )}

      {/* Main content */}
      {data && !isLoading && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<SavingsIcon />} iconColor="#10B981" iconBg="#ECFDF5"
                title="Potential Savings"
                value={fmt(data.summary?.totalPotentialSavings)}
                subtitle="estimated monthly"
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<OpportunityIcon />} iconColor="#2563EB" iconBg="#EFF6FF"
                title="Opportunities Found"
                value={data.summary?.opportunityCount?.toString() || '0'}
                subtitle={`across ${Object.keys(data.opportunities?.reduce((acc, o) => { acc[o.service] = true; return acc; }, {}) || {}).length} services`}
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<HighPriorityIcon />} iconColor="#EF4444" iconBg="#FEF2F2"
                title="High Priority"
                value={data.summary?.highPriority?.toString() || '0'}
                subtitle="require immediate attention"
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<OpportunityIcon />} iconColor="#F59E0B" iconBg="#FFFBEB"
                title="Top Category"
                value={CATEGORY_LABELS[data.summary?.topCategory] || data.summary?.topCategory || '-'}
                subtitle="most common opportunity type"
              />
            </Grid>
          </Grid>

          {/* Filters */}
          <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  )
                }}
                sx={{ minWidth: 220, '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
                  sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                  <MenuItem value="ALL">All Priorities</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                  sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                  <MenuItem value="ALL">All Categories</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat} sx={{ fontSize: '0.8125rem' }}>
                      {CATEGORY_LABELS[cat] || cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Chip
                label={`${filteredOpportunities.length} result${filteredOpportunities.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ fontSize: '0.75rem', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}
              />
            </CardContent>
          </Card>

          {/* Opportunities Table */}
          {filteredOpportunities.length === 0 ? (
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>No Opportunities Found</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                  {data.opportunities?.length === 0
                    ? 'Your AWS usage is well-optimized. No cost savings opportunities detected for the selected period.'
                    : 'No opportunities match your current filters. Try adjusting the search or filter criteria.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: 32 }} />
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Opportunity</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Service</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Est. Savings</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOpportunities.map((opp, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow
                        hover
                        onClick={() => toggleRow(idx)}
                        sx={{ cursor: 'pointer', '&:last-child td': expandedRows[idx] ? {} : { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ width: 32, px: 1.5 }}>
                          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                            {expandedRows[idx] ? <ChevronUp /> : <ChevronDown />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
                            {opp.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {opp.service}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={CATEGORY_LABELS[opp.category] || opp.category}
                            size="small"
                            sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600, bgcolor: alpha('#2563EB', 0.08), color: '#2563EB' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#10B981' }}>
                            {fmt(opp.estimatedSavings)}/mo
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={opp.priority}
                            size="small"
                            sx={{
                              fontSize: '0.625rem', height: 20, fontWeight: 700,
                              bgcolor: alpha(PRIORITY_COLORS[opp.priority] || '#6B7280', 0.1),
                              color: PRIORITY_COLORS[opp.priority] || '#6B7280'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      {/* Expanded detail row */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedRows[idx] ? undefined : 'none' }}>
                          <Collapse in={expandedRows[idx]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.text.primary, 0.015) }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                                    Description
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary', lineHeight: 1.7, mb: 2 }}>
                                    {opp.description}
                                  </Typography>

                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                                    Impact Assessment
                                  </Typography>
                                  <Chip
                                    label={opp.impact}
                                    size="small"
                                    sx={{
                                      fontSize: '0.6875rem', height: 22, fontWeight: 600,
                                      bgcolor: opp.impact?.toLowerCase().includes('no downtime') || opp.impact?.toLowerCase().includes('no changes')
                                        ? alpha('#10B981', 0.1) : alpha('#F59E0B', 0.1),
                                      color: opp.impact?.toLowerCase().includes('no downtime') || opp.impact?.toLowerCase().includes('no changes')
                                        ? '#10B981' : '#F59E0B'
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                                    Remediation Command
                                  </Typography>
                                  <Box sx={{ position: 'relative' }}>
                                    <Box sx={{
                                      p: 1.5, borderRadius: '8px',
                                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                                      border: '1px solid', borderColor: 'divider',
                                      fontFamily: 'monospace', fontSize: '0.6875rem', lineHeight: 1.6,
                                      wordBreak: 'break-all', color: 'text.primary'
                                    }}>
                                      {opp.remediation}
                                    </Box>
                                    <Tooltip title="Copy command">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(opp.remediation); }}
                                        sx={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24 }}
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Service Cost</Typography>
                                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{fmt(opp.serviceCost)}</Typography>
                                    </Box>
                                    {opp.trend !== undefined && opp.trend !== 0 && (
                                      <Box>
                                        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Trend</Typography>
                                        <Typography sx={{
                                          fontSize: '0.8125rem', fontWeight: 600,
                                          color: opp.trend >= 0 ? '#EF4444' : '#10B981'
                                        }}>
                                          {opp.trend >= 0 ? '+' : ''}{opp.trend.toFixed(1)}%
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Cached indicator */}
          {data.cached && (
            <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', mt: 1.5, textAlign: 'right' }}>
              Results cached · Click refresh for latest analysis
            </Typography>
          )}
        </>
      )}
      </>
      )}

      {/* ═══ TAB 1: Rightsizing ═══ */}
      {activeTab === 1 && (
      <>
        {/* Rightsizing Loading */}
        {isLoadingRightsizing && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={36} sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Analyzing instance utilization metrics...</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 1 }}>This may take a moment as we query CloudWatch metrics for each instance.</Typography>
          </Box>
        )}

        {/* Rightsizing Error */}
        {rightsizingError && !isLoadingRightsizing && (
          <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>{rightsizingError}</Alert>
        )}

        {/* Rightsizing Content */}
        {rightsizingData && !isLoadingRightsizing && (
          <>
            {/* Metrics Source Banner */}
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 2, bgcolor: alpha('#2563EB', 0.02) }}>
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Chip
                    label={`Metrics: ${rightsizingData.summary?.metricsSource === 'prometheus+cloudwatch' ? 'Prometheus + CloudWatch' : 'CloudWatch'}`}
                    size="small"
                    sx={{ fontSize: '0.6875rem', fontWeight: 600, bgcolor: alpha('#2563EB', 0.1), color: '#2563EB' }}
                  />
                  {!rightsizingData.summary?.hasMemoryData && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Memory metrics unavailable — install CloudWatch Agent or configure Prometheus for full analysis
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => { setConfigDialogOpen(true); loadMetricsConfig(); }}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem' }}>
                    Configure Prometheus
                  </Button>
                  <Tooltip title="Re-analyze">
                    <IconButton size="small" onClick={() => loadRightsizing(true)} disabled={isLoadingRightsizing}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>

            {/* Rightsizing Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} lg={3}>
                <StatCard icon={<SavingsIcon />} iconColor="#10B981" iconBg="#ECFDF5"
                  title="Potential Savings" value={fmt(rightsizingData.summary?.totalEstimatedSavings)} subtitle="from rightsizing" />
              </Grid>
              <Grid item xs={6} lg={3}>
                <StatCard icon={<OpportunityIcon />} iconColor="#2563EB" iconBg="#EFF6FF"
                  title="Resources Analyzed"
                  value={(rightsizingData.summary?.totalResources || 0).toString()}
                  subtitle={[
                    rightsizingData.summary?.ec2Count && `${rightsizingData.summary.ec2Count} EC2`,
                    rightsizingData.summary?.rdsCount && `${rightsizingData.summary.rdsCount} RDS`,
                    rightsizingData.summary?.elasticacheCount && `${rightsizingData.summary.elasticacheCount} ElastiCache`,
                  ].filter(Boolean).join(' · ') || 'no resources found'}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <StatCard icon={<HighPriorityIcon />} iconColor="#EF4444" iconBg="#FEF2F2"
                  title="Overprovisioned"
                  value={((rightsizingData.summary?.underutilized || 0) + (rightsizingData.summary?.overprovisioned || 0)).toString()}
                  subtitle="can be downsized" />
              </Grid>
              <Grid item xs={6} lg={3}>
                <StatCard icon={<OpportunityIcon />} iconColor="#10B981" iconBg="#ECFDF5"
                  title="Right-sized" value={(rightsizingData.summary?.rightSized || 0).toString()} subtitle="optimal utilization" />
              </Grid>
            </Grid>

            {/* Rightsizing Table */}
            {rightsizingData.recommendations?.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Resource</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Kind</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Instance Type</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>CPU (avg/max)</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Memory</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Finding</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Suggestion</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rightsizingData.recommendations.map((rec, idx) => (
                      <TableRow key={idx} hover>
                        {/* Resource name */}
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {rec.instanceName || rec.instanceId}
                          </Typography>
                          {rec.engine && (
                            <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}>{rec.engine}</Typography>
                          )}
                        </TableCell>
                        {/* Resource type badge */}
                        <TableCell>
                          <Chip
                            label={rec.resourceType || 'EC2'}
                            size="small"
                            sx={{
                              fontSize: '0.6rem', height: 20, fontWeight: 700,
                              bgcolor: rec.resourceType === 'RDS' ? alpha('#8B5CF6', 0.1)
                                : rec.resourceType === 'ElastiCache' ? alpha('#F59E0B', 0.1)
                                : alpha('#2563EB', 0.1),
                              color: rec.resourceType === 'RDS' ? '#8B5CF6'
                                : rec.resourceType === 'ElastiCache' ? '#F59E0B'
                                : '#2563EB'
                            }}
                          />
                        </TableCell>
                        {/* Instance type */}
                        <TableCell>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                            {rec.instanceType}
                          </Typography>
                        </TableCell>
                        {/* CPU bar */}
                        <TableCell>
                          {rec.metrics?.cpu?.avg !== null && rec.metrics?.cpu?.avg !== undefined ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(rec.metrics.cpu.avg, 100)}
                                  sx={{
                                    width: 48, height: 6, borderRadius: 3,
                                    bgcolor: alpha(theme.palette.text.primary, 0.08),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 3,
                                      bgcolor: rec.metrics.cpu.avg < 20 ? '#EF4444'
                                        : rec.metrics.cpu.avg < 40 ? '#F59E0B' : '#10B981'
                                    }
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                  {rec.metrics.cpu.avg}% / {rec.metrics.cpu.max}%
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>{rec.metrics.cpu.source}</Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>N/A</Typography>
                          )}
                        </TableCell>
                        {/* Memory bar */}
                        <TableCell>
                          {rec.metrics?.memory?.avg !== null && rec.metrics?.memory?.avg !== undefined ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(rec.metrics.memory.avg, 100)}
                                sx={{
                                  width: 48, height: 6, borderRadius: 3,
                                  bgcolor: alpha(theme.palette.text.primary, 0.08),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    bgcolor: rec.metrics.memory.avg < 30 ? '#EF4444'
                                      : rec.metrics.memory.avg < 50 ? '#F59E0B' : '#10B981'
                                  }
                                }}
                              />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{rec.metrics.memory.avg}%</Typography>
                            </Box>
                          ) : (
                            <Tooltip title={
                              rec.resourceType === 'EC2'
                                ? 'Install CloudWatch Agent or configure Prometheus for memory metrics'
                                : rec.resourceType === 'RDS'
                                ? 'FreeableMemory reported via CloudWatch'
                                : 'BytesUsedForCache reported via CloudWatch'
                            }>
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', cursor: 'help' }}>
                                {(rec.resourceType === 'RDS' || rec.resourceType === 'ElastiCache') ? 'No data' : 'No agent'}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        {/* Finding chip */}
                        <TableCell>
                          <Chip
                            label={FINDING_LABELS[rec.finding] || rec.finding}
                            size="small"
                            sx={{
                              fontSize: '0.625rem', height: 20, fontWeight: 700,
                              bgcolor: alpha(FINDING_COLORS[rec.finding] || '#6B7280', 0.1),
                              color: FINDING_COLORS[rec.finding] || '#6B7280'
                            }}
                          />
                        </TableCell>
                        {/* Suggested type */}
                        <TableCell>
                          {rec.suggestedType ? (
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#2563EB', fontFamily: 'monospace' }}>
                              → {rec.suggestedType}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {rec.finding === 'RIGHT_SIZED' ? 'No change' : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        {/* Savings */}
                        <TableCell align="right">
                          {rec.estimatedMonthlySavings > 0 ? (
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#10B981' }}>
                              {fmt(rec.estimatedMonthlySavings)}/mo
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>No Running Instances Found</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    No EC2 instances are currently running in this account, or metrics are not yet available.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {rightsizingData.cached && (
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', mt: 1.5, textAlign: 'right' }}>
                Results cached · Click refresh for latest analysis
              </Typography>
            )}
          </>
        )}

        {/* No account selected */}
        {!selectedAccount && !isLoadingRightsizing && (
          <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Select an AWS account to analyze instance rightsizing.</Typography>
            </CardContent>
          </Card>
        )}
      </>
      )}

      {/* Prometheus Configuration Dialog */}
      <Dialog open={configDialogOpen} onClose={() => { setConfigDialogOpen(false); setTestResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Configure Metrics Source</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 2 }}>
            Connect a Prometheus endpoint to get memory and disk utilization metrics for more accurate rightsizing recommendations.
            Without Prometheus, only CPU metrics from CloudWatch are available.
          </Typography>

          {metricsConfig && metricsConfig.length > 0 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '0.8125rem' }}>
              Current config: {metricsConfig[0].provider} — {metricsConfig[0].endpoint} ({metricsConfig[0].is_active ? 'Active' : 'Inactive'})
            </Alert>
          )}

          {/* Connection Test Result */}
          {testResult && (
            <Alert
              severity={testResult.status === 'success' ? 'success' : 'error'}
              sx={{ mb: 2, borderRadius: '8px', fontSize: '0.8125rem' }}
              onClose={() => setTestResult(null)}
            >
              {testResult.message}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <Select value={configForm.provider} onChange={(e) => { setConfigForm(prev => ({ ...prev, provider: e.target.value })); setTestResult(null); }}
                sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                <MenuItem value="prometheus">Prometheus</MenuItem>
                <MenuItem value="thanos">Thanos</MenuItem>
                <MenuItem value="mimir">Grafana Mimir</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth size="small" label="Endpoint URL"
              placeholder="https://prometheus.example.com"
              value={configForm.endpoint}
              onChange={(e) => { setConfigForm(prev => ({ ...prev, endpoint: e.target.value })); setTestResult(null); }}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }}
              helperText="The Prometheus-compatible API endpoint (must be reachable from the backend)"
            />
            <FormControl fullWidth size="small">
              <Select value={configForm.authType} onChange={(e) => { setConfigForm(prev => ({ ...prev, authType: e.target.value })); setTestResult(null); }}
                sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                <MenuItem value="none">No Authentication</MenuItem>
                <MenuItem value="bearer">Bearer Token</MenuItem>
                <MenuItem value="basic">Basic Auth (user:pass)</MenuItem>
              </Select>
            </FormControl>
            {configForm.authType !== 'none' && (
              <TextField
                fullWidth size="small"
                label={configForm.authType === 'bearer' ? 'Bearer Token' : 'Username:Password'}
                placeholder={configForm.authType === 'bearer' ? 'your-token-here' : 'username:password'}
                type="password"
                value={configForm.authToken}
                onChange={(e) => { setConfigForm(prev => ({ ...prev, authToken: e.target.value })); setTestResult(null); }}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => { setConfigDialogOpen(false); setTestResult(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={testConnection}
              disabled={!configForm.endpoint || isTesting || isSavingConfig}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              variant="contained"
              onClick={saveMetricsConfig}
              disabled={!configForm.endpoint || isSavingConfig || isTesting}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              {isSavingConfig ? 'Saving...' : 'Save & Test'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CostOpportunities;
